/* eslint-disable max-statements */
import { useEffect, useMemo, useReducer, useState } from 'react';
import { IDataItemDefinition, IFilterDefinition } from '@zencity/reporting-types';
import { PlatformUser, Roles } from '@zencity/customer-management-client';
import { logger } from 'utils/logger';
import { WidgetBundleDefinition } from 'screens/WidgetGenerator/reportGeneratorComponents/dataItemDefinitions';
import { buildFilterDefinitions } from 'screens/WidgetGenerator/reportGeneratorComponents/filterDefinitions';
import '../../../i18n';
import 'index.scss';
import {
  initialState,
  reducer,
  SurveysReportBuilderConfigActionsType,
} from 'screens/WidgetGenerator/reportGeneratorComponents/useSurveysReportGeneratorConfig.reducers';
import {
  buildDataItemDefinitions,
  getRequiredSurveysDataReportBuilder,
} from 'screens/WidgetGenerator/reportGeneratorComponents/useSurveysReportGeneratorConfig.helpers';
import { ReportBuilderClientData } from 'screens/WidgetGenerator/reportGeneratorComponents/types';

interface SurveysReportGeneratorConfig {
  dataItemDefinitions: (IDataItemDefinition | WidgetBundleDefinition)[];
  filterDefinitions: IFilterDefinition[];
  isLoading?: boolean;
}

// eslint-disable-next-line max-lines-per-function
const useSurveysReportGeneratorConfig = (
  zcClientId: string,
  platformUser: PlatformUser,
): SurveysReportGeneratorConfig => {
  const isSuperAdmin = platformUser.roles.includes(Roles.ZENCITY_SUPERADMIN);
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getClientAndSurveysGroupsData = async () => {
      const { client, surveysGroups, rakeWeighterExecutionsBySurveyGroup, divisionTreeBySurveyType } =
        await getRequiredSurveysDataReportBuilder(zcClientId);

      dispatch({
        type: SurveysReportBuilderConfigActionsType.UPDATE_STATE,
        payload: {
          surveysClient: client,
          surveysGroups,
          rakeWeighterExecutionsBySurveyGroup,
          divisionTreeBySurveyType,
        },
      });
    };

    getClientAndSurveysGroupsData()
      .catch(logger.error)
      .finally(() => setIsLoading(false));
  }, [zcClientId]);

  const isReady = useMemo(() => state.surveysClient?.zc_client_id && state.surveysGroups.length > 0, [state]);

  if (!isReady || !state.surveysClient) {
    return {
      dataItemDefinitions: [],
      filterDefinitions: [],
      isLoading,
    };
  }

  const clientSurveysData: ReportBuilderClientData = {
    client: state.surveysClient,
    surveysGroups: state.surveysGroups,
    rakeWeighterExecutionsBySurveyGroup: state.rakeWeighterExecutionsBySurveyGroup,
    divisionTreeBySurveyType: state.divisionTreeBySurveyType,
  };

  return {
    dataItemDefinitions: buildDataItemDefinitions(isSuperAdmin, clientSurveysData),
    filterDefinitions: buildFilterDefinitions(clientSurveysData),
  };
};

export default useSurveysReportGeneratorConfig;
